import { useEffect, useState } from 'react';
import { Video, Plus, Pencil, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { useApi, type HomeCamera } from '../lib/api';
import { CameraEditModal } from './CameraEditModal';

interface Props {
  homeId: string;
}

/**
 * Cameras for a single home. Mirrors the IntegrationsList style — card with
 * a header row, a small "Add" affordance, and a table of rows below. Edit/delete
 * are inline; Add opens a modal.
 */
export function CamerasList({ homeId }: Props) {
  const api = useApi();
  const [cameras, setCameras] = useState<HomeCamera[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<HomeCamera | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const { cameras } = await api.listHomeCameras(homeId);
      setCameras(cameras);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeId]);

  const onAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const onEdit = (cam: HomeCamera) => {
    setEditing(cam);
    setModalOpen(true);
  };

  const onDelete = async (cam: HomeCamera) => {
    if (!confirm(`Delete camera "${cam.label}" (${cam.cameraId})? This cannot be undone.`)) return;
    setDeletingId(cam.cameraId);
    setError(null);
    try {
      await api.deleteHomeCamera(homeId, cam.cameraId);
      setCameras((prev) => (prev ?? []).filter((c) => c.cameraId !== cam.cameraId));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const onSaved = (saved: HomeCamera) => {
    setCameras((prev) => {
      if (!prev) return [saved];
      const idx = prev.findIndex((c) => c.cameraId === saved.cameraId);
      if (idx === -1) return [...prev, saved];
      const next = prev.slice();
      next[idx] = saved;
      return next;
    });
    setModalOpen(false);
    setEditing(null);
  };

  return (
    <div className="mb-8 bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-baseline justify-between mb-4 gap-2">
        <h2 className="font-semibold text-lg text-brand-ink inline-flex items-center gap-2">
          <Video size={18} className="text-slate-500" /> Cameras
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600">
            {cameras ? `${cameras.length} ${cameras.length === 1 ? 'camera' : 'cameras'}` : '—'}
          </span>
          <button
            onClick={refresh}
            disabled={loading}
            className="text-slate-600 hover:text-brand-ink p-1 rounded hover:bg-slate-100 disabled:opacity-40"
            aria-label="Refresh cameras"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs bg-brand-ink hover:bg-ink-800 text-brand-bone"
          >
            <Plus size={12} /> Add Camera
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2.5 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
          {error}
        </div>
      )}

      {cameras === null && loading && (
        <div className="text-xs text-slate-600 inline-flex items-center gap-2 py-2">
          <Loader2 size={12} className="animate-spin" /> Loading cameras…
        </div>
      )}

      {cameras !== null && cameras.length === 0 && (
        <div className="text-xs text-slate-600 py-2">
          No cameras configured. Click "Add Camera" to register the first one.
        </div>
      )}

      {cameras !== null && cameras.length > 0 && (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.16em] text-slate-600">
                <th className="text-left font-medium px-2 pb-2">ID</th>
                <th className="text-left font-medium px-2 pb-2">Label</th>
                <th className="text-left font-medium px-2 pb-2">Role</th>
                <th className="text-left font-medium px-2 pb-2">Codec</th>
                <th className="px-2 pb-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {cameras.map((c) => (
                <tr key={c.cameraId} className="border-t border-slate-200">
                  <td className="px-2 py-2 font-mono text-xs text-brand-ink align-middle">
                    {c.cameraId}
                  </td>
                  <td className="px-2 py-2 text-brand-ink align-middle">
                    <div>{c.label}</div>
                    {c.portrait && (
                      <div className="text-[10px] text-slate-600 mt-0.5">portrait</div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-ink-800 align-middle">{c.role}</td>
                  <td className="px-2 py-2 align-middle">
                    <CodecBadge codec={c.codec} />
                  </td>
                  <td className="px-2 py-2 align-middle text-right whitespace-nowrap">
                    <button
                      onClick={() => onEdit(c)}
                      className="text-slate-600 hover:text-brand-ink p-1 rounded hover:bg-slate-100"
                      aria-label={`Edit camera ${c.label}`}
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(c)}
                      disabled={deletingId === c.cameraId}
                      className="text-slate-600 hover:text-alert-breach p-1 rounded hover:bg-red-50 disabled:opacity-40"
                      aria-label={`Delete camera ${c.label}`}
                      title="Delete"
                    >
                      {deletingId === c.cameraId ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <CameraEditModal
          homeId={homeId}
          camera={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function CodecBadge({ codec }: { codec: HomeCamera['codec'] }) {
  // h265 is the modern default; we tint it brass to make scanning the table easier.
  const cls =
    codec === 'h265'
      ? 'bg-slate-100 text-slate-600 border-slate-200'
      : 'bg-slate-50 text-ink-800 border-slate-300';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase ${cls}`}>
      {codec}
    </span>
  );
}
